import React, { useState, useEffect, useCallback, useRef } from 'react';
import { signOut, onAuthStateChanged } from "firebase/auth";
import {
  doc, getDoc, collection, getDocs, query, updateDoc, Timestamp,
  addDoc, where, orderBy, serverTimestamp
} from "firebase/firestore";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db, fetchRemoteConfig } from '../firebase-config';
import { downloadReceiptAsPdf, downloadReceiptAsImage, shareReceipt, formatPaymentCategory, generateReceiptHtml } from './ReceiptGenerator';
import SubscriptionPaywall from './SubscriptionPaywall';
import { SecureLog, PaymentEvents, SubscriptionEvents, AuthEvents } from '../utils/SecurityLogger';
import THEME from '../theme';
import { motion, AnimatePresence } from 'framer-motion';
import SupportTicketSystem from './SupportTicketSystem';
import ComplaintBox from './ComplaintBox';

// ======================== MODULE SYSTEM ========================
// Map of module keys (must match ResidoModule enum in Kotlin and Firestore)
const MODULE_NAMES = {
  BILLING: 'Billing',
  PAYMENT: 'Online Payment',
  REBATE: 'Mess Rebate',
  DIET_CHANGE: 'Diet Change',
  COMPLAINTS: 'Complaints',
  SUPPORT_TICKETS: 'Support Tickets',
  NOTIFICATIONS: 'Notifications',
  LOAN_STUDENTS: 'Wallet / Loan',
  AUTO_FINE: 'Auto Fine',
  BUDDY: 'Buddy / Friend',
  ANNOUNCEMENTS: 'Announcements',
  MESS_COMMITTEE: 'Mess Committee',
  ROOM_ALLOCATION: 'Room Allocation',
};

// The Module Disabled Modal shown instead of hiding UI
function ModuleDisabledModal({ moduleKey, onClose }) {
  const moduleName = MODULE_NAMES[moduleKey] || moduleKey;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative bg-slate-900 border border-slate-700 rounded-3xl p-8 max-w-sm w-full mx-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Lock Icon */}
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 mx-auto mb-5">
          <svg className="w-8 h-8 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
            <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3V12.75a3 3 0 00-3-3v-3A5.25 5.25 0 0012 1.5zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" />
          </svg>
        </div>
        <h3 className="text-white font-bold text-xl text-center mb-2">Feature Not Available</h3>
        <p className="text-slate-400 text-sm text-center mb-3">
          The <span className="text-amber-400 font-semibold">{moduleName}</span> feature hasn't been enabled for your institute.
        </p>
        <div className="bg-slate-800 rounded-xl px-4 py-3 text-xs text-slate-500 text-center mb-6">
          Please contact your hostel administrator for more information.
        </div>
        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl font-bold text-white bg-amber-500 hover:bg-amber-600 transition-colors"
        >
          Got It
        </button>
      </div>
    </div>
  );
}

// --- CONSTANTS ---
const COOLDOWN_MILLIS = 1 * 60 * 60 * 1000; // 1 hour (matches Android)

// ======================== SECURITY HELPERS ========================

// [SECURITY] Sanitize user-editable text (name editing, feedback)
// Strips HTML tags and dangerous characters to prevent XSS
function sanitizeInput(str) {
  if (!str) return '';
  return str
    .replace(/<[^>]*>/g, '') // Strip HTML tags
    .replace(/[<>"'&]/g, '') // Strip dangerous chars
    .trim();
}

// [SECURITY] Validate display name (alphanumeric + spaces + common chars)
function sanitizeName(name) {
  if (!name) return '';
  return name
    .replace(/<[^>]*>/g, '')
    .replace(/[^a-zA-Z0-9\s.\-']/g, '')
    .trim()
    .substring(0, 50);
}

// [SECURITY] Validate payment amounts client-side
function validatePaymentAmount(bill) {
  const total = (bill?.billAmount || 0) + (bill?.fineAmount || 0);
  if (total <= 0) return { valid: false, reason: 'Invalid bill amount.' };
  if (total < 1) return { valid: false, reason: 'Amount too small to process.' };
  if (total > 100000) return { valid: false, reason: 'Amount exceeds maximum limit (₹1,00,000).' };
  if (!Number.isFinite(total)) return { valid: false, reason: 'Invalid amount value.' };
  return { valid: true, total };
}

// [SECURITY] Idempotency tracking — prevent duplicate payment submissions
const IDEMPOTENCY_STORE_KEY = 'resido_payment_idem';
function getProcessedPayments() {
  try {
    return JSON.parse(sessionStorage.getItem(IDEMPOTENCY_STORE_KEY) || '{}');
  } catch { return {}; }
}
function markPaymentProcessed(billId, orderId) {
  const store = getProcessedPayments();
  store[billId] = { orderId, timestamp: Date.now() };
  sessionStorage.setItem(IDEMPOTENCY_STORE_KEY, JSON.stringify(store));
}
function isPaymentAlreadyProcessing(billId) {
  const store = getProcessedPayments();
  if (!store[billId]) return false;
  // Consider stale after 5 min
  return (Date.now() - store[billId].timestamp) < 5 * 60 * 1000;
}

// [SECURITY] Firebase Auth token refresh check
async function ensureTokenFresh() {
  try {
    const user = auth.currentUser;
    if (user) await user.getIdToken(true); // Force refresh
    return true;
  } catch (err) {
    SecureLog.warn('[Auth] Token refresh failed:', err.message);
    return false;
  }
}

// ======================== ANIMATION VARIANTS ========================
// NOTE: Using simple CSS transitions instead of AnimatePresence mode='wait'
// to prevent white-flash during tab switches (AnimatePresence unmounts
// the old content before the new content mounts, causing a blank frame).

// --- HELPER: App Logo ---
const AppLogo = () => (
  <div className="flex items-center gap-3 px-2">
    <div className="relative w-8 h-8 flex items-center justify-center">
      <div className="absolute inset-0 bg-white/10 backdrop-blur-xl rounded-full border border-white/20"></div>
      <img
        src="/assets/ic_placeholder_avatar.png"
        alt="Resido Logo"
        className="relative z-10 w-full h-full rounded-full object-cover p-[2px]"
      />
    </div>
    <span style={{ fontFamily: THEME.fonts.heading }} className="text-xl font-bold text-white tracking-tight">
      Resido
    </span>
  </div>
);

// --- HELPER: Sidebar Item ---
const SidebarItem = ({ icon, label, active, onClick, destructive }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${active
      ? 'bg-amber-500/10 text-amber-500'
      : destructive
        ? 'text-red-400 hover:bg-red-500/10'
        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
      }`}
  >
    <div className={`p-1.5 rounded-lg ${active ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30' : 'bg-transparent'}`}>
      {icon}
    </div>
    <span className="font-medium text-sm">{label}</span>
  </button>
);

function Dashboard() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bills, setBills] = useState([]);
  const [userData, setUserData] = useState(null);
  const [walletData, setWalletData] = useState(null);

  // [MODULE SYSTEM] enabledModules: null = loading/unknown, [] = no modules, [..] = specific list
  // null means fail-open (treat all as available)
  const [enabledModules, setEnabledModules] = useState(null);
  // [INSTITUTION-PAYS] Store institute subscription data for paywall check
  const [instituteData, setInstituteData] = useState(null);
  // When non-null, shows the ModuleDisabledModal with this module key
  const [moduleDisabledKey, setModuleDisabledKey] = useState(null);

  // [MODULE SYSTEM] Check if a module is available for this institute
  // Returns true if: modules not loaded yet (fail-open), or key is in list
  const isModuleEnabled = useCallback((moduleKey) => {
    if (enabledModules === null) return true; // fail-open while loading
    return enabledModules.includes(moduleKey);
  }, [enabledModules]);

  // [MODULE SYSTEM] Guard wrapper: calls action() if module enabled, else shows dialog
  const requireModule = useCallback((moduleKey, action) => {
    if (isModuleEnabled(moduleKey)) {
      action();
    } else {
      setModuleDisabledKey(moduleKey);
    }
  }, [isModuleEnabled]);

  // Navigation State
  const [activeTab, setActiveTab] = useState('home'); // 'home', 'history', 'settings'
  const [settingsSubView, setSettingsSubView] = useState(null); // null, 'profile', 'committee', 'feedback', 'terms', 'privacy', 'disclaimer'

  // Committee State
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [committeeLoading, setCommitteeLoading] = useState(false);

  // Feedback State
  const [feedbackSubject, setFeedbackSubject] = useState('');
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackToast, setFeedbackToast] = useState(null); // { type: 'success'|'error', message }

  // Receipt Preview State
  const [previewBill, setPreviewBill] = useState(null);

  // Name Editing State
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState('');

  // Payment Confirmation Dialog State
  const [paymentConfirmBill, setPaymentConfirmBill] = useState(null);

  // Diet Change State
  const [isDietWindowOpen, setIsDietWindowOpen] = useState(false);
  const [dietRequestStatus, setDietRequestStatus] = useState(null); // 'PENDING' | 'APPROVED' | 'REJECTED' | null
  const [showDietDialog, setShowDietDialog] = useState(false);
  const [dietSubmitting, setDietSubmitting] = useState(false);
  const [dietToast, setDietToast] = useState(null);

  // History Search, Sort & Collapse State
  const [historySearch, setHistorySearch] = useState('');
  const [expandedBillId, setExpandedBillId] = useState(null);
  const [historySortOrder, setHistorySortOrder] = useState('newest'); // 'newest' | 'oldest' | 'amount_high' | 'amount_low'

  // Sign Out Confirmation
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);

  // Hostel Mess Bills State
  const [hostelMessBills, setHostelMessBills] = useState([]);
  const [messBillsLoading, setMessBillsLoading] = useState(false);
  const [selectedMessBill, setSelectedMessBill] = useState(null);

  // Refresh Animation State
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Payment State
  const [paymentState, setPaymentState] = useState('idle'); // 'idle' | 'checking_health' | 'creating_session' | 'redirecting'
  const [payingBillId, setPayingBillId] = useState(null);
  const [showServerOffline, setShowServerOffline] = useState(false);
  const [paymentBanner, setPaymentBanner] = useState(null); // { type: 'success'|'pending'|'failed', message, orderId }

  // Cooldown timers state: { billId: remainingMs }
  const [cooldownTimers, setCooldownTimers] = useState({});

  // PWA Install State
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // General App State
  const [appVersionInfo, setAppVersionInfo] = useState(null);

  // [ROOM ALLOCATION] Room assignment data
  const [roomData, setRoomData] = useState(null);
  const [roommates, setRoommates] = useState([]);

  // [ROOM CHANGE REQUEST]
  const [roomChangeRequest, setRoomChangeRequest] = useState(null); // existing request
  const [roomChangeForm, setRoomChangeForm] = useState({ reason: '', preferredRoom: '' });
  const [submittingRoomChange, setSubmittingRoomChange] = useState(false);
  const [roomChangeWindowOpen, setRoomChangeWindowOpen] = useState(false);

  // Remote Config values
  const remoteConfigRef = useRef({ hdfcBackendBaseUrl: '', healthCheckEndpoints: [] });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // --- COOLDOWN TIMER MANAGEMENT ---
  useEffect(() => {
    // Initialize cooldown timers from bills
    const timers = {};
    bills.forEach(bill => {
      if (!bill.paid && bill.lastPaymentAttempt) {
        const attemptTime = bill.lastPaymentAttempt.seconds * 1000;
        const remaining = (attemptTime + COOLDOWN_MILLIS) - Date.now();
        if (remaining > 0) {
          timers[bill.id] = remaining;
        }
      }
    });
    setCooldownTimers(timers);
  }, [bills]);

  // Tick down cooldown timers every second
  useEffect(() => {
    const hasActive = Object.values(cooldownTimers).some(ms => ms > 0);
    if (!hasActive) return;

    const interval = setInterval(() => {
      setCooldownTimers(prev => {
        const updated = {};
        let anyActive = false;
        for (const [id, ms] of Object.entries(prev)) {
          const remaining = ms - 1000;
          if (remaining > 0) {
            updated[id] = remaining;
            anyActive = true;
          }
        }
        return anyActive ? updated : {};
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownTimers]);

  // --- HEALTH CHECK ---
  const runHealthChecks = useCallback(async (endpoints) => {
    if (!endpoints || endpoints.length === 0) return true;

    const results = await Promise.all(
      endpoints.map(async (url) => {
        if (!url || !url.startsWith('https://')) return false;
        try {
          const cacheBuster = url.includes('?') ? `&_t=${Date.now()}` : `?_t=${Date.now()}`;
          const response = await fetch(`${url}${cacheBuster}`, { method: 'GET', mode: 'cors' });
          if (!response.ok) {
            console.error('Health check failed for', url, 'Status:', response.status);
          }
          return response.ok;
        } catch (e) {
          console.error('Health check network error for', url, e);
          return false;
        }
      })
    );
    return results.every(r => r);
  }, []);

  // --- PAYMENT HANDLER ---
  const handlePayNow = useCallback(async (bill) => {
    if (paymentState !== 'idle') return;

    // [SECURITY] Idempotency check
    if (isPaymentAlreadyProcessing(bill.id)) {
      SecureLog.warn('[Payment] Duplicate submission blocked for bill:', bill.id);
      setPaymentBanner({ type: 'pending', message: 'A payment for this bill is already being processed.', orderId: null });
      return;
    }

    // [SECURITY] Validate payment amount
    const validation = validatePaymentAmount(bill);
    if (!validation.valid) {
      setPaymentBanner({ type: 'failed', message: validation.reason, orderId: null });
      return;
    }

    // [SECURITY] Refresh auth token before payment
    const tokenOk = await ensureTokenFresh();
    if (!tokenOk) {
      setPaymentBanner({ type: 'failed', message: 'Session expired. Please refresh and try again.', orderId: null });
      return;
    }

    // 1. Check cooldown
    const remaining = cooldownTimers[bill.id];
    if (remaining && remaining > 0) return;

    // 2. Get remote config
    const config = remoteConfigRef.current;
    if (!config.hdfcBackendBaseUrl) {
      setPaymentBanner({ type: 'failed', message: 'Configuration Error: Payment backend URL not configured.', orderId: null });
      return;
    }

    // 3. Run health checks
    setPaymentState('checking_health');
    setPayingBillId(bill.id);

    // Minimum 2.5s display (matching Android's UX)
    const healthStartTime = Date.now();
    const isHealthy = await runHealthChecks(config.healthCheckEndpoints);
    const elapsed = Date.now() - healthStartTime;
    if (elapsed < 2500) {
      await new Promise(resolve => setTimeout(resolve, 2500 - elapsed));
    }

    if (!isHealthy) {
      setPaymentState('idle');
      setPayingBillId(null);
      setShowServerOffline(true);
      PaymentEvents.serverOffline();
      return;
    }

    // 4. Update lastPaymentAttempt on Firestore
    try {
      const billRef = doc(db, "users", user.uid, "messBills", bill.id);
      await updateDoc(billRef, { lastPaymentAttempt: Timestamp.now() });

      // Update local state immediately for cooldown
      setCooldownTimers(prev => ({ ...prev, [bill.id]: COOLDOWN_MILLIS }));
    } catch (error) {
      SecureLog.error("Failed to update lastPaymentAttempt:", error);
    }

    // 5. Create payment session via backend (same endpoint as Android)
    setPaymentState('creating_session');

    try {
      const orderId = crypto.randomUUID().replace(/-/g, '').substring(0, 20).toUpperCase();
      const totalAmount = validation.total.toFixed(2);

      // [SECURITY] Mark as processing for idempotency
      markPaymentProcessed(bill.id, orderId);

      // [NEW] Log initiation to backend
      PaymentEvents.initiated(bill.id, parseFloat(totalAmount), orderId, config.hdfcBackendBaseUrl);

      const paymentType = 'PAYMENT_LINK_SELF';

      let endpoint = config.hdfcBackendBaseUrl;
      if (endpoint.endsWith('/')) endpoint = endpoint.slice(0, -1);
      if (!endpoint.includes('/api/v2')) endpoint += '/api/v2/create-session';
      else if (!endpoint.endsWith('/create-session')) endpoint += '/create-session';

      const idempotencyKey = crypto.randomUUID();

      const requestBody = {
        institute_id: userData.instituteId,
        order_id: orderId,
        amount: totalAmount,
        customer_id: user.uid,
        customer_email: userData.email || user.email,
        customer_phone: userData.contact || '9999999999',
        bill_month: bill.id,
        payment_type: paymentType,
        payer_email: userData.email || user.email,
        recipient_email: userData.email || user.email,
        hostel: userData.hostel,
        payment_channel: 'WEB_DASHBOARD', // [NEW] Explicit channel
        screen_resolution: `${window.screen.width}x${window.screen.height}` // [NEW] Metadata
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const sessionData = await response.json();

      // 6. Extract payment link from session response
      // The backend now forwards payment_links from Juspay's response
      let paymentUrl = null;

      if (sessionData.payment_links?.web) {
        paymentUrl = sessionData.payment_links.web;
      } else if (sessionData.sdk_payload?.payload?.paymentLinks?.web) {
        paymentUrl = sessionData.sdk_payload.payload.paymentLinks.web;
      }

      if (!paymentUrl) {
        throw new Error('Payment link not available in server response. Please contact support.');
      }

      // 7. Redirect to payment URL
      setPaymentState('redirecting');
      PaymentEvents.redirected(orderId, config.hdfcBackendBaseUrl); // [NEW] Log redirect
      window.location.href = paymentUrl;

    } catch (error) {
      SecureLog.error("Payment session creation failed:", error);
      // [NEW] Log failure with orderId if available (it might be undefined if error happened before generation)
      // crypto.randomUUID is robust, so orderId likely exists if we passed line 246. 
      // But scope of 'orderId' is inside try block. We can't access it here easily unless we lift it.
      // However, PaymentEvents.failed logic in SecurityLogger handles null orderId gracefully.
      // We'll pass null for now as orderId is block-scoped, unless we refactor.
      // Refactoring slightly to access orderId would be better, but for now we log the error reason.
      // Actually, let's just log the error message.
      PaymentEvents.failed(null, error.message);

      setPaymentBanner({
        type: 'failed',
        message: error.message || 'Failed to create payment session. Please try again.',
        orderId: null,
      });
    }
    setPaymentState('idle');
    setPayingBillId(null);
  }, [paymentState, cooldownTimers, user, userData, runHealthChecks]);

  // --- HANDLE PAYMENT RETURN (from HDFC redirect) ---
  useEffect(() => {
    const status = searchParams.get('payment_status');
    const orderId = searchParams.get('order_id');

    if (status && orderId) {
      // Redirect to the dedicated payment status page
      navigate(`/payment-status?order_id=${encodeURIComponent(orderId)}&payment_status=${encodeURIComponent(status)}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // --- PWA INSTALLATION HANDLER ---
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallApp = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      SecureLog.info('User installed PWA');
    }
  };

  // --- DATA FETCHING LOGIC ---
  const fetchDashboardData = async (currentUser) => {
    try {
      // 1. Fetch User Data
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }

      // 2. Fetch Bills (Sub-collection)
      const billsRef = collection(db, "users", currentUser.uid, "messBills");
      const q = query(billsRef);
      const querySnapshot = await getDocs(q);
      const billsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Client-side sort by billIssuedOn if available
      billsData.sort((a, b) => (b.billIssuedOn?.seconds || 0) - (a.billIssuedOn?.seconds || 0));
      setBills(billsData);

      // 3. Fetch Wallet Data (LoanStudents collection)
      const walletRef = doc(db, "loanStudents", currentUser.uid);
      const walletSnap = await getDoc(walletRef);
      if (walletSnap.exists()) {
        setWalletData(walletSnap.data());
      }

      // 4. Fetch Remote Config (health endpoints + backend URL)
      const config = await fetchRemoteConfig();
      remoteConfigRef.current = config;

      // 5. [MODULE SYSTEM] Fetch institute modules config
      const freshUserData = (await getDoc(doc(db, 'users', currentUser.uid))).data();
      const instituteId = freshUserData?.instituteId;
      if (instituteId) {
        try {
          const instSnap = await getDoc(doc(db, 'institutes', instituteId));
          if (instSnap.exists()) {
            const modules = instSnap.data()?.modules;
            if (modules?.enabledModules) {
              setEnabledModules(modules.enabledModules); // e.g. ['BILLING', 'PAYMENT', ...]
            } else {
              setEnabledModules(null); // no modules field → fail-open
            }
            // [INSTITUTION-PAYS] Store subscription data from institute doc
            setInstituteData(instSnap.data());
          }
        } catch (modErr) {
          SecureLog.warn('[Modules] Failed to fetch institute modules, defaulting to open:', modErr);
          setEnabledModules(null); // fail-open on error
        }
      }

      // 6. [ROOM ALLOCATION] Fetch room assignment if user has roomId
      const latestUserData = (await getDoc(doc(db, 'users', currentUser.uid))).data();
      if (latestUserData?.roomId && latestUserData?.instituteId) {
        try {
          const roomRef = doc(db, 'institutes', latestUserData.instituteId, 'rooms', latestUserData.roomId);
          const roomSnap = await getDoc(roomRef);
          if (roomSnap.exists()) {
            const rd = roomSnap.data();
            setRoomData({ id: roomSnap.id, ...rd });
            // Fetch roommate names
            const occupants = (rd.occupants || []).filter(uid => uid !== currentUser.uid);
            if (occupants.length > 0) {
              const roommateData = await Promise.all(
                occupants.map(async (uid) => {
                  try {
                    const uDoc = await getDoc(doc(db, 'users', uid));
                    if (!uDoc.exists()) return { uid, name: 'Unknown' };
                    const d = uDoc.data();
                    return {
                      uid,
                      name: d.name || d.displayName || 'Unknown',
                      diet: d.diet || '',
                      batch: d.batch || '',
                      contact: d.contact || '',
                      isPrivate: d.isPrivate || false,
                      photoUrl: d.photoUrl || null,
                    };
                  } catch { return { uid, name: 'Unknown' }; }
                })
              );
              setRoommates(roommateData);
            } else {
              setRoommates([]);
            }
          }

          // Fetch existing room change request
          try {
            const reqQuery = query(
              collection(db, 'roomChangeRequests'),
              where('userId', '==', currentUser.uid),
              orderBy('createdAt', 'desc')
            );
            const reqSnap = await getDocs(reqQuery);
            if (!reqSnap.empty) {
              const latest = reqSnap.docs[0];
              setRoomChangeRequest({ id: latest.id, ...latest.data() });
            }
          } catch (rcErr) {
            SecureLog.warn('[RoomChangeReq] Failed to fetch:', rcErr);
          }

          // Fetch room change window status from institute
          if (userData?.instituteId) {
            try {
              const instDoc = await getDoc(doc(db, 'institutes', userData.instituteId));
              if (instDoc.exists()) {
                setRoomChangeWindowOpen(instDoc.data().roomChangeWindowOpen || false);
              }
            } catch (rcwErr) {
              SecureLog.warn('[RoomChangeWindow] Failed to fetch:', rcwErr);
            }
          }
        } catch (roomErr) {
          SecureLog.warn('[Room] Failed to fetch room data:', roomErr);
        }
      }

    } catch (error) {
      SecureLog.error("Error fetching dashboard data:", error);
    }
  };

  // --- FETCH COMMITTEE MEMBERS ---
  const fetchCommitteeMembers = useCallback(async () => {
    if (!userData?.instituteId || !userData?.hostel) return;
    setCommitteeLoading(true);
    try {
      const q = query(
        collection(db, 'messCommittee'),
        where('instituteId', '==', userData.instituteId),
        where('hostel', '==', userData.hostel)
      );
      const snap = await getDocs(q);
      const members = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      members.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCommitteeMembers(members);
    } catch (err) {
      SecureLog.error('Failed to fetch committee members:', err);
    } finally {
      setCommitteeLoading(false);
    }
  }, [userData]);

  // --- SUBMIT FEEDBACK ---
  const handleSubmitFeedback = useCallback(async () => {
    if (!feedbackDescription.trim() || feedbackSending) return;
    setFeedbackSending(true);
    try {
      // [SECURITY] Sanitize feedback inputs
      const safeSubject = sanitizeInput(feedbackSubject).substring(0, 100);
      const safeDescription = sanitizeInput(feedbackDescription).substring(0, 1000);
      if (!safeDescription) {
        setFeedbackToast({ type: 'error', message: 'Description cannot be empty after sanitization.' });
        return;
      }
      await addDoc(collection(db, 'feedback'), {
        userId: user?.uid || 'anon',
        userEmail: userData?.email || user?.email || 'anon',
        subject: safeSubject,
        description: safeDescription,
        timestamp: Timestamp.now(),
        appVersion: 'web-1.1',
        status: 'new'
      });
      setFeedbackSubject('');
      setFeedbackDescription('');
      setFeedbackToast({ type: 'success', message: 'Feedback submitted successfully!' });
      setTimeout(() => setFeedbackToast(null), 4000);
    } catch (err) {
      SecureLog.error('Failed to submit feedback:', err);
      setFeedbackToast({ type: 'error', message: 'Failed to submit. Please try again.' });
      setTimeout(() => setFeedbackToast(null), 4000);
    } finally {
      setFeedbackSending(false);
    }
  }, [feedbackSubject, feedbackDescription, feedbackSending, user, userData]);

  // --- DIET CHANGE: Fetch window & status (mirrors Android AuthViewModel.fetchDietChangeWindowAndStatus) ---
  const fetchDietChangeStatus = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const windowDoc = await getDoc(doc(db, 'settings', 'dietChangeWindow'));
      if (windowDoc.exists()) {
        const start = windowDoc.data().startDate?.toDate?.();
        const end = windowDoc.data().endDate?.toDate?.();
        const now = new Date();

        if (start && end) {
          setIsDietWindowOpen(now > start && now < end);

          // Query existing requests by this user
          const requestQuery = await getDocs(
            query(collection(db, 'dietChangeRequests'), where('userId', '==', uid))
          );

          // Find the latest request created AFTER the window started
          const requestsInWindow = requestQuery.docs
            .filter(d => {
              const ts = d.data().timestamp?.toDate?.();
              return ts && ts > start;
            })
            .sort((a, b) => (b.data().timestamp?.toDate?.()?.getTime() || 0) - (a.data().timestamp?.toDate?.()?.getTime() || 0));

          if (requestsInWindow.length > 0) {
            setDietRequestStatus(requestsInWindow[0].data().status);
          } else {
            setDietRequestStatus(null);
          }
        } else {
          setIsDietWindowOpen(false);
          setDietRequestStatus(null);
        }
      } else {
        setIsDietWindowOpen(false);
        setDietRequestStatus(null);
      }
    } catch {
      setIsDietWindowOpen(false);
      setDietRequestStatus(null);
    }
  }, []);

  // --- DIET CHANGE: Submit request (mirrors Android AuthViewModel.submitDietChangeRequest) ---
  const handleSubmitDietChange = useCallback(async (targetDiet) => {
    if (!user || !userData || dietSubmitting) return;
    setDietSubmitting(true);
    try {
      await addDoc(collection(db, 'dietChangeRequests'), {
        userId: user.uid,
        userName: userData.name || '',
        userEmail: userData.email || user.email || '',
        hostel: userData.hostel || '',
        currentDiet: userData.diet || '',
        batch: userData.batch || '',
        requestedDiet: targetDiet,
        status: 'PENDING',
        timestamp: Timestamp.now()
      });
      setDietRequestStatus('PENDING');
      setShowDietDialog(false);
      setDietToast({ type: 'success', message: 'Diet change request submitted!' });
      setTimeout(() => setDietToast(null), 4000);
    } catch (err) {
      SecureLog.error('Failed to submit diet change request:', err);
      setShowDietDialog(false);
      setDietToast({ type: 'error', message: `Failed: ${err.message}` });
      setTimeout(() => setDietToast(null), 4000);
    } finally {
      setDietSubmitting(false);
    }
  }, [user, userData, dietSubmitting]);

  // --- Fetch Hostel Mess Bills ---
  const fetchHostelMessBills = useCallback(async () => {
    if (!userData?.hostel || messBillsLoading) return;
    setMessBillsLoading(true);
    try {
      const q = query(
        collection(db, 'hostelMessBills'),
        where('hostelName', '==', userData.hostel),
        orderBy('yearMonth', 'desc')
      );
      const snapshot = await getDocs(q);
      const bills = snapshot.docs.map(doc => ({ documentId: doc.id, ...doc.data() }));
      setHostelMessBills(bills);
    } catch (err) {
      SecureLog.error('Failed to fetch hostel mess bills:', err);
      setHostelMessBills([]);
    } finally {
      setMessBillsLoading(false);
    }
  }, [userData, messBillsLoading]);

  // Auto-fetch committee when sub-view is selected
  useEffect(() => {
    if (settingsSubView === 'committee' && committeeMembers.length === 0) {
      fetchCommitteeMembers();
    }
  }, [settingsSubView, committeeMembers.length, fetchCommitteeMembers]);

  // Auto-fetch mess bills when sub-view is selected
  useEffect(() => {
    if (settingsSubView === 'messBills' && hostelMessBills.length === 0) {
      fetchHostelMessBills();
    }
  }, [settingsSubView, hostelMessBills.length, fetchHostelMessBills]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Check if user has a complete profile before showing dashboard
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists() || !userDoc.data().isPhoneVerified) {
          // No profile or incomplete profile → redirect to profile setup
          setLoading(false);
          navigate('/institute-selection');
          return;
        }

        if (userDoc.data().userType === 'android') {
          // Android user trying to access web dashboard → sign out and redirect
          await signOut(auth);
          setLoading(false);
          navigate('/login');
          return;
        }

        setUser(currentUser);
        await fetchDashboardData(currentUser);
        fetchDietChangeStatus(currentUser.uid);
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = () => {
    signOut(auth).then(() => { AuthEvents.logout(); navigate('/'); }).catch(SecureLog.error);
  };

  // --- REFRESH HANDLER (also serves as pull-to-refresh) ---
  const handleRefreshData = useCallback(async () => {
    if (user && !isRefreshing) {
      setIsRefreshing(true);
      await Promise.all([
        fetchDashboardData(user),
        new Promise(resolve => setTimeout(resolve, 1000))
      ]);
      setIsRefreshing(false);
    }
  }, [user, isRefreshing]);

  // --- PULL-TO-REFRESH (touch gesture for mobile) ---
  // Only activates when the scrollable main container is at the very top
  const touchStartY = useRef(0);
  const isPullGesture = useRef(false);
  const mainRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    // Only track if we're at the very top of the scroll container
    const container = mainRef.current;
    if (container && container.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      isPullGesture.current = true;
    } else {
      isPullGesture.current = false;
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!isPullGesture.current || isRefreshing) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (deltaY > 100) { // Increased threshold to avoid false triggers
      handleRefreshData();
    }
    isPullGesture.current = false;
  }, [isRefreshing, handleRefreshData]);

  // --- COOLDOWN DISPLAY HELPER ---
  const formatCooldown = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const pendingBillsCount = bills.filter(b => !b.paid).length;
  const clearedBillsCount = bills.filter(b => b.paid).length;
  const hasActiveWallet = walletData && walletData.status === 'ACTIVE';

  // --- SUBSCRIPTION CHECK (Institution-Pays Model) ---
  // Subscription is now on the institute document, not the individual user.
  const isSubscriptionActive = () => {
    const sub = instituteData?.subscription;
    if (!sub || sub.status !== 'active') return false;
    const expiresAt = sub.expiresAt;
    if (!expiresAt) return false;
    let expiryDate;
    if (expiresAt?.seconds) {
      expiryDate = new Date(expiresAt.seconds * 1000);
    } else if (expiresAt instanceof Date) {
      expiryDate = expiresAt;
    } else if (typeof expiresAt === 'string') {
      expiryDate = new Date(expiresAt);
    } else {
      return false;
    }
    return expiryDate > new Date();
  };

  // Show paywall if institute subscription is NOT active
  if (userData && instituteData && !isSubscriptionActive()) {
    SubscriptionEvents.expired();
    return (
      <SubscriptionPaywall
        userData={userData}
        onLogout={() => navigate('/')}
        tierName={instituteData?.subscription?.plan || 'free'}
        onSubscriptionActivated={() => {
          // Refresh dashboard data to pick up updated institute subscription
          if (user) fetchDashboardData(user);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex bg-[#0F172A] font-sans text-slate-200">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Poppins:wght@500;600;700;800&display=swap');
        
        @keyframes spin-double {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(720deg); }
        }
        .animate-spin-double {
            animation: spin-double 1s ease-in-out;
        }

        @keyframes pulse-glow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>

      {/* --- CONTENT AREA (full width, with bottom padding for nav) --- */}
      <main
        ref={mainRef}
        className="flex-1 p-4 sm:p-6 md:p-10 pb-32 relative overflow-y-auto"
        style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))', transform: 'translateZ(0)' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull-to-Refresh Indicator */}
        <AnimatePresence>
          {isRefreshing && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 40 }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center justify-center mb-4"
            >
              <div className="flex items-center gap-2 text-amber-500 text-sm">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                <span className="font-medium">Refreshing...</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Top Bar with logo and refresh */}
        <div className="flex items-center justify-between mb-8">
          <AppLogo />
          <button
            onClick={handleRefreshData}
            className={`p-2 rounded-xl transition-all ${isRefreshing ? 'text-amber-500 cursor-not-allowed bg-amber-500/10' : 'text-slate-400 hover:text-amber-500 hover:bg-slate-800'}`}
            title="Refresh"
            disabled={isRefreshing}
          >
            <svg className={`w-5 h-5 transition-transform duration-700 ${isRefreshing ? 'animate-spin' : 'hover:rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* --- PWA INSTALL BANNER --- */}
        {deferredPrompt && (
          <div className="mb-6 p-4 rounded-xl border bg-indigo-500/10 border-indigo-500/30 text-indigo-300 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 flex-shrink-0 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <div>
                <span className="text-sm font-bold text-indigo-200 block">Install Resido App</span>
                <span className="text-xs text-indigo-300/80">Get a better experience with our mobile app.</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleInstallApp} className="px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold rounded-lg transition-colors shadow-md shadow-indigo-500/20">
                Install
              </button>
              <button onClick={() => setDeferredPrompt(null)} className="p-1 text-indigo-400 hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
        )}

        {/* --- PAYMENT RETURN BANNER --- */}
        {paymentBanner && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center justify-between ${paymentBanner.type === 'success' ? 'bg-teal-500/10 border-teal-500/30 text-teal-400' :
            paymentBanner.type === 'pending' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
              'bg-red-500/10 border-red-500/30 text-red-400'
            }`}>
            <div className="flex items-center gap-3">
              {paymentBanner.type === 'success' && (
                <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              )}
              {paymentBanner.type === 'pending' && (
                <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              )}
              {paymentBanner.type === 'failed' && (
                <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              )}
              <span className="text-sm font-medium">{paymentBanner.message}</span>
            </div>
            <button onClick={() => setPaymentBanner(null)} className="ml-4 flex-shrink-0 opacity-60 hover:opacity-100">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}

        {/* --- VIEW SWITCHER (simple CSS transitions — no AnimatePresence to avoid white flash) --- */}
        {activeTab === 'home' && (
          <div className="animate-fade-in-up">
            <>
              <header className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                  <h1 style={{ fontFamily: THEME.fonts.heading }} className="text-2xl sm:text-3xl font-bold text-white">
                    Dashboard
                  </h1>
                  <p className="text-slate-400 mt-1 text-sm sm:text-base">
                    Welcome back, <span className="text-amber-500 font-semibold">{userData?.name || user?.email}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {userData?.diet && (
                    <div className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${userData.diet === 'Veg'
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                      : 'bg-orange-500/10 border-orange-500/30 text-orange-400'
                      }`}>
                      {userData.diet === 'Veg' ? '🥬' : '🍗'} {userData.diet}
                    </div>
                  )}
                  {userData?.course && (
                    <div className="px-3 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-xs font-medium text-indigo-300">
                      {userData.course}
                    </div>
                  )}
                  {userData?.batch && (
                    <div className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs font-medium text-slate-300">
                      Batch: <span className="text-white">{userData.batch}</span>
                    </div>
                  )}
                </div>
              </header>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                <StatCard
                  title="Pending Bills"
                  value={pendingBillsCount}
                  icon={<svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                  color="red"
                />
                <StatCard
                  title="Cleared Bills"
                  value={clearedBillsCount}
                  icon={<svg className="w-6 h-6 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                  color="teal"
                />
                {hasActiveWallet ? (
                  <StatCard
                    title="Wallet Balance"
                    value={`₹${walletData.balance.toFixed(0)}`}
                    icon={<svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>}
                    color="amber"
                    subtext="Status: Active"
                  />
                ) : (
                  <div className="bg-[#1E293B] p-6 rounded-2xl border border-dashed border-slate-700 flex flex-col items-center justify-center text-center">
                    <p className="text-slate-500 text-sm mb-2">No Active Wallet</p>
                    <p className="text-xs text-slate-600">Contact admin to activate prepaid wallet.</p>
                  </div>
                )}
              </div>

              {/* Pending Bills Section */}
              <section className="bg-[#1E293B] rounded-2xl border border-slate-700/50 overflow-hidden shadow-xl">
                <div className="p-4 sm:p-6 border-b border-slate-700/50">
                  <h2 style={{ fontFamily: THEME.fonts.heading }} className="text-lg sm:text-xl font-bold text-white">Pending Bills</h2>
                </div>

                {/* Table for larger screens */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left text-sm text-slate-400">
                    <thead className="bg-slate-900/50 text-xs uppercase font-semibold text-slate-300">
                      <tr>
                        <th className="px-6 py-4">Bill Period</th>
                        <th className="px-6 py-4">Amount</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {bills.filter(b => !b.paid).length > 0 ? (
                        bills.filter(b => !b.paid).map((bill) => {
                          const cooldownMs = cooldownTimers[bill.id] || 0;
                          const isOnCooldown = cooldownMs > 0;
                          const isThisBillProcessing = payingBillId === bill.id && paymentState !== 'idle';

                          return (
                            <tr key={bill.id} className="hover:bg-slate-800/50 transition-colors">
                              <td className="px-6 py-4 font-medium text-white">{bill.id}</td>
                              <td className="px-6 py-4 text-slate-200">
                                ₹{bill.billAmount}
                                {(bill.fineAmount > 0) && (
                                  <span className="text-red-400 text-xs ml-2">(+₹{bill.fineAmount} fine)</span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20">
                                  Pending
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <button
                                  onClick={() => requireModule('PAYMENT', () => setPaymentConfirmBill(bill))}
                                  disabled={isOnCooldown || isThisBillProcessing || paymentState !== 'idle'}
                                  className={`font-bold py-1.5 px-4 rounded-lg text-xs shadow-lg transition-all active:scale-95 ${isOnCooldown
                                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed shadow-none'
                                    : isThisBillProcessing
                                      ? 'bg-amber-500/50 text-slate-900 cursor-wait shadow-amber-500/10'
                                      : paymentState !== 'idle'
                                        ? 'bg-slate-700 text-slate-500 cursor-not-allowed shadow-none'
                                        : 'bg-amber-500 hover:bg-amber-600 text-slate-900 shadow-amber-500/20'
                                    }`}
                                >
                                  {isThisBillProcessing ? (
                                    <span className="flex items-center gap-2">
                                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                      {paymentState === 'checking_health' ? 'Checking...' : paymentState === 'creating_session' ? 'Creating...' : 'Redirecting...'}
                                    </span>
                                  ) : isOnCooldown ? (
                                    `Retry in ${formatCooldown(cooldownMs)}`
                                  ) : (
                                    'Pay Now'
                                  )}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-6 py-12 text-center text-slate-500">
                            <div className="flex flex-col items-center gap-2">
                              <svg className="w-10 h-10 text-teal-500/40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <span>All caught up! No pending bills.</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Card layout for mobile */}
                <div className="sm:hidden">
                  {bills.filter(b => !b.paid).length > 0 ? (
                    <div className="divide-y divide-slate-700/50">
                      {bills.filter(b => !b.paid).map((bill) => {
                        const cooldownMs = cooldownTimers[bill.id] || 0;
                        const isOnCooldown = cooldownMs > 0;
                        const isThisBillProcessing = payingBillId === bill.id && paymentState !== 'idle';
                        return (
                          <div key={bill.id} className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-white font-semibold text-sm">{bill.id}</h4>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/10 text-red-400 border border-red-500/20">Pending</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-slate-200 text-sm font-medium">₹{bill.billAmount}</span>
                                {(bill.fineAmount > 0) && (
                                  <span className="text-red-400 text-xs ml-1">(+₹{bill.fineAmount})</span>
                                )}
                              </div>
                              <button
                                onClick={() => requireModule('PAYMENT', () => setPaymentConfirmBill(bill))}
                                disabled={isOnCooldown || isThisBillProcessing || paymentState !== 'idle'}
                                className={`font-bold py-1.5 px-4 rounded-lg text-xs transition-all active:scale-95 ${isOnCooldown
                                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                                  : isThisBillProcessing
                                    ? 'bg-amber-500/50 text-slate-900 cursor-wait'
                                    : paymentState !== 'idle'
                                      ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                      : 'bg-amber-500 hover:bg-amber-600 text-slate-900'
                                  }`}
                              >
                                {isThisBillProcessing ? 'Processing...' : isOnCooldown ? formatCooldown(cooldownMs) : 'Pay Now'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-slate-500">
                      <svg className="w-10 h-10 mx-auto mb-2 text-teal-500/40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <span className="text-sm">All caught up!</span>
                    </div>
                  )}
                </div>
              </section>
            </>
          </div>
        )}

        {/* --- PROFILE TAB (under Settings) --- */}
        {activeTab === 'settings' && settingsSubView === 'profile' && userData && (
          <div className="max-w-2xl mx-auto animate-fade-in-up">
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => setSettingsSubView(null)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all" title="Back">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              </button>
              <h2 style={{ fontFamily: THEME.fonts.heading }} className="text-2xl font-bold text-white">Edit Profile</h2>
            </div>

            {/* Diet Toast */}
            {dietToast && (
              <div className={`mb-4 p-4 rounded-xl border flex items-center gap-3 ${dietToast.type === 'success' ? 'bg-teal-500/10 border-teal-500/30 text-teal-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
                <span className="text-sm font-medium">{dietToast.message}</span>
              </div>
            )}
            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

              <div className="flex flex-col md:flex-row items-start gap-8 relative z-10">
                <div className="w-24 h-24 rounded-full bg-slate-800 border-2 border-amber-500/50 p-1 flex-shrink-0 overflow-hidden">
                  <img
                    src={userData.photoUrl || "/assets/user_avatar.png"}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/assets/user_avatar.png"; }}
                  />
                </div>

                <div className="flex-1 w-full">
                  {/* Editable Name */}
                  {isEditingName ? (
                    <div className="mb-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => { setEditedName(e.target.value); setNameError(''); }}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded-xl px-4 py-2.5 text-white text-lg font-bold focus:outline-none focus:border-amber-500 transition-colors"
                          placeholder="Enter your name"
                          autoFocus
                          maxLength={50}
                        />
                      </div>
                      {nameError && <p className="text-red-400 text-xs mt-1.5 ml-1">{nameError}</p>}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={async () => {
                            const trimmed = sanitizeName(editedName);
                            if (!trimmed) { setNameError('Name cannot be empty.'); return; }
                            if (trimmed.length < 2) { setNameError('Name must be at least 2 characters.'); return; }
                            if (/[<>"']/.test(editedName)) { setNameError('Name contains invalid characters.'); return; }
                            if (trimmed === userData.name) { setIsEditingName(false); return; }
                            setIsSavingName(true);
                            try {
                              const userRef = doc(db, 'users', user.uid);
                              await updateDoc(userRef, { name: trimmed });
                              setUserData(prev => ({ ...prev, name: trimmed }));
                              setIsEditingName(false);
                            } catch (err) {
                              SecureLog.error('Failed to update name:', err);
                              setNameError('Failed to save. Please try again.');
                            } finally {
                              setIsSavingName(false);
                            }
                          }}
                          disabled={isSavingName}
                          className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm rounded-xl transition-all disabled:opacity-50"
                        >
                          {isSavingName ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => { setIsEditingName(false); setNameError(''); }}
                          disabled={isSavingName}
                          className="px-5 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium text-sm rounded-xl transition-all disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-2xl font-bold text-white">{userData.name}</h3>
                      <button
                        onClick={() => { setEditedName(userData.name || ''); setIsEditingName(true); }}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-amber-400 hover:bg-slate-800 transition-all"
                        title="Edit Name"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <p className="text-slate-400 mb-6 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    {userData.email}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <ProfileDetail label="Hostel" value={userData.hostel} />
                    <ProfileDetail label="Batch" value={userData.batch} />
                    <ProfileDetail label="Phone" value={userData.contact} />
                    <ProfileDetail label="Gender" value={userData.gender} />
                  </div>

                  {/* Diet Change Card */}
                  <div className="mt-6 p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Diet Preference</p>
                        <p className="text-amber-400 font-medium">{userData.diet || 'Not Set'}</p>
                      </div>
                      {dietRequestStatus === 'PENDING' ? (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">Pending</span>
                      ) : dietRequestStatus === 'APPROVED' ? (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-teal-500/10 text-teal-400 border border-teal-500/20">Approved</span>
                      ) : dietRequestStatus === 'REJECTED' ? (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20">Rejected</span>
                      ) : isDietWindowOpen ? (
                        <button
                          onClick={() => requireModule('DIET_CHANGE', () => setShowDietDialog(true))}
                          className="px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-xl text-xs font-bold hover:bg-amber-500/20 transition-all"
                        >Request Change</button>
                      ) : (
                        <span className="text-xs text-slate-600">Window closed</span>
                      )}
                    </div>
                    {dietRequestStatus === 'PENDING' && (
                      <p className="text-xs text-slate-500 mt-2">Your diet change request is awaiting admin approval.</p>
                    )}
                    {!isDietWindowOpen && !dietRequestStatus && (
                      <p className="text-xs text-slate-600 mt-2">Diet change requests are currently not being accepted.</p>
                    )}
                  </div>

                  {hasActiveWallet && (
                    <div className="mt-8 p-4 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-between">
                      <div>
                        <p className="text-teal-400 text-xs font-bold uppercase tracking-wider">Mess Wallet Active</p>
                        <p className="text-white font-mono text-xl">₹{walletData.balance}</p>
                      </div>
                      <div className="h-10 w-10 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- PAYMENT HISTORY TAB --- */}
        {activeTab === 'history' && (() => {
          const paidBills = bills.filter(b => b.paid);

          // [FEATURE] Sort bills
          const sortedPaidBills = [...paidBills].sort((a, b) => {
            switch (historySortOrder) {
              case 'oldest': return (a.paymentDate?.seconds || 0) - (b.paymentDate?.seconds || 0);
              case 'amount_high': return ((b.paidAmount || 0) + (b.loanDeductionApplied || 0)) - ((a.paidAmount || 0) + (a.loanDeductionApplied || 0));
              case 'amount_low': return ((a.paidAmount || 0) + (a.loanDeductionApplied || 0)) - ((b.paidAmount || 0) + (b.loanDeductionApplied || 0));
              case 'newest':
              default: return (b.paymentDate?.seconds || 0) - (a.paymentDate?.seconds || 0);
            }
          });

          const filteredBills = historySearch.trim()
            ? sortedPaidBills.filter(b =>
              (b.id || '').toLowerCase().includes(historySearch.toLowerCase()) ||
              (b.paymentId || '').toLowerCase().includes(historySearch.toLowerCase()) ||
              (b.transactionId || '').toLowerCase().includes(historySearch.toLowerCase()) ||
              String(b.paidAmount || '').includes(historySearch) ||
              String(b.billAmount || '').includes(historySearch)
            )
            : sortedPaidBills;

          return (
            <div className="max-w-4xl mx-auto animate-fade-in-up">
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <h2 style={{ fontFamily: THEME.fonts.heading }} className="text-xl sm:text-2xl font-bold text-white whitespace-nowrap">Payment History</h2>
                <div className="flex items-center gap-2 flex-1 max-w-md">
                  {/* [FEATURE] Sort dropdown */}
                  <select
                    value={historySortOrder}
                    onChange={(e) => setHistorySortOrder(e.target.value)}
                    className="bg-slate-800/50 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors cursor-pointer appearance-none"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2394A3B8\' stroke-width=\'2\'%3E%3Cpath d=\'M6 9l6 6 6-6\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: '30px' }}
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="amount_high">Amount ↑</option>
                    <option value="amount_low">Amount ↓</option>
                  </select>
                  <div className="relative flex-1">
                    <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input
                      type="text"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder="Search bills..."
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-9 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 transition-colors"
                    />
                    {historySearch && (
                      <button onClick={() => setHistorySearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Total Paid Summary */}
              {paidBills.length > 0 && !historySearch && (
                <div className="bg-teal-500/10 border border-teal-500/20 rounded-2xl p-4 mb-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-teal-400/70 text-xs font-medium">Total Paid Lifetime</p>
                    <p className="text-white font-bold text-lg">₹{paidBills.reduce((sum, b) => sum + (b.paidAmount || 0) + (b.loanDeductionApplied || 0), 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              )}

              {/* Bills List */}
              {filteredBills.length > 0 ? (
                <div className="space-y-3">
                  {filteredBills.map((bill) => {
                    const isExpanded = expandedBillId === bill.id;
                    const isVerificationFailed = bill.bankVerificationFailed === true;
                    return (
                      <div
                        key={bill.id}
                        className={`bg-[#1E293B] rounded-2xl border shadow-lg overflow-hidden transition-all ${isVerificationFailed ? 'border-red-500/30' : 'border-slate-700/50'
                          }`}
                      >
                        {/* Collapsed Header — always visible */}
                        <button
                          onClick={() => !isVerificationFailed && setExpandedBillId(isExpanded ? null : bill.id)}
                          className="w-full p-4 sm:p-5 flex items-center justify-between text-left"
                          disabled={isVerificationFailed}
                        >
                          <div className="min-w-0">
                            <h3 className="text-sm sm:text-base font-bold text-white">{bill.id}</h3>
                            <p className={`text-xs mt-0.5 ${isVerificationFailed ? 'text-red-400 font-semibold' : 'text-slate-500'}`}>
                              {isVerificationFailed
                                ? 'Verification Failed'
                                : bill.paymentDate
                                  ? `Paid on ${new Date(bill.paymentDate.seconds * 1000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                                  : 'Date N/A'
                              }
                            </p>
                          </div>
                          {isVerificationFailed ? (
                            <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          ) : (
                            <svg className={`w-5 h-5 text-amber-500 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                          )}
                        </button>

                        {/* Expanded Content */}
                        {isExpanded && (
                          <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-slate-700/50 pt-4 space-y-4">
                            {/* Amount Details */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-slate-900/50 p-3 rounded-lg">
                                <p className="text-[10px] sm:text-xs text-slate-500 mb-1">Bill Amount</p>
                                <p className="text-white font-semibold text-sm">₹{bill.billAmount || 0}</p>
                              </div>
                              {(bill.fineAmount > 0) && (
                                <div className="bg-slate-900/50 p-3 rounded-lg">
                                  <p className="text-[10px] sm:text-xs text-slate-500 mb-1">Fine</p>
                                  <p className="text-red-400 font-semibold text-sm">₹{bill.fineAmount}</p>
                                </div>
                              )}
                              <div className="bg-slate-900/50 p-3 rounded-lg">
                                <p className="text-[10px] sm:text-xs text-slate-500 mb-1">Total Paid</p>
                                <p className="text-teal-400 font-bold text-sm">₹{(bill.paidAmount || 0) + (bill.loanDeductionApplied || 0)}</p>
                              </div>
                              <div className="bg-slate-900/50 p-3 rounded-lg">
                                <p className="text-[10px] sm:text-xs text-slate-500 mb-1">Method</p>
                                <p className="text-slate-200 font-medium text-xs sm:text-sm">{bill.paymentMethod || 'Online'}</p>
                              </div>
                            </div>

                            {/* Transaction Details */}
                            <div className="bg-slate-900/30 rounded-xl p-3 sm:p-4 space-y-2 text-xs sm:text-sm">
                              <div className="flex justify-between gap-2"><span className="text-slate-500">Order ID</span><span className="text-slate-300 font-mono text-[10px] sm:text-xs truncate max-w-[50%]">{bill.paymentId || 'N/A'}</span></div>
                              <div className="flex justify-between gap-2"><span className="text-slate-500">Transaction ID</span><span className="text-slate-300 font-mono text-[10px] sm:text-xs truncate max-w-[50%]">{bill.transactionId || 'N/A'}</span></div>
                              <div className="flex justify-between gap-2"><span className="text-slate-500">Category</span><span className="text-slate-300 text-xs">{formatPaymentCategory(bill.paymentCategory)}</span></div>
                              <div className="flex justify-between gap-2"><span className="text-slate-500">Paid By</span><span className="text-slate-300 text-xs">{bill.paidBy || 'Self'}</span></div>
                              {(bill.loanDeductionApplied > 0) && (
                                <div className="flex justify-between gap-2"><span className="text-slate-500">Loan Adjusted</span><span className="text-amber-400 text-xs">₹{bill.loanDeductionApplied}</span></div>
                              )}
                            </div>

                            {/* Receipt Actions */}
                            {bill.bankVerified === true ? (
                              <div className="flex gap-2 flex-wrap">
                                <button onClick={() => downloadReceiptAsPdf(bill, userData, bill.id)} className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-xl text-xs font-medium hover:bg-amber-500/20 transition-all">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                  Download
                                </button>
                                <button onClick={() => shareReceipt(bill, userData, bill.id)} className="flex items-center gap-1.5 px-3 py-2 bg-teal-500/10 border border-teal-500/30 text-teal-400 rounded-xl text-xs font-medium hover:bg-teal-500/20 transition-all">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                                  Share
                                </button>
                                <button onClick={() => setPreviewBill(bill)} className="flex items-center gap-1.5 px-3 py-2 bg-slate-700/50 border border-slate-600/30 text-slate-300 rounded-xl text-xs font-medium hover:bg-slate-700 transition-all">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  Preview
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-slate-500 text-xs italic bg-slate-900/30 rounded-xl px-3 py-2.5">
                                <svg className="w-4 h-4 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                <span>Receipt locked — awaiting verification</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : historySearch ? (
                <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-12 text-center shadow-xl">
                  <svg className="w-12 h-12 mx-auto mb-3 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  <p className="text-slate-500 text-sm">No matching bills found</p>
                </div>
              ) : (
                <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-12 text-center shadow-xl">
                  <svg className="w-16 h-16 mx-auto mb-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <p className="text-slate-500">No payment history yet.</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* --- MESS COMMITTEE TAB (under Settings) --- */}
        {activeTab === 'settings' && settingsSubView === 'committee' && (
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => setSettingsSubView(null)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all" title="Back">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              </button>
              <h2 style={{ fontFamily: THEME.fonts.heading }} className="text-2xl font-bold text-white">Mess Committee</h2>
            </div>

            {committeeLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500"></div>
              </div>
            ) : committeeMembers.length > 0 ? (
              <div className="space-y-3">
                {committeeMembers.map((member) => (
                  <div key={member.id} className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 flex items-center gap-4 shadow-lg hover:border-slate-600 transition-colors">
                    <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                      {member.photoUrl ? (
                        <img src={member.photoUrl} alt={member.name} className="w-full h-full rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null}
                      <svg className={`w-6 h-6 text-amber-500 ${member.photoUrl ? 'hidden' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">{member.name}</p>
                      <p className="text-slate-400 text-sm">{member.designation}</p>
                    </div>
                    {member.phoneNumber && (
                      <a
                        href={`https://wa.me/${member.phoneNumber.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 text-green-400 rounded-xl text-sm font-medium hover:bg-green-500/20 transition-all flex-shrink-0"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                        WhatsApp
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-12 text-center shadow-xl">
                <svg className="w-16 h-16 mx-auto mb-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                <p className="text-slate-500">No committee members found for your hostel.</p>
              </div>
            )}
          </div>
        )}

        {/* --- FEEDBACK TAB (under Settings) --- */}
        {activeTab === 'settings' && settingsSubView === 'feedback' && (
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => setSettingsSubView(null)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all" title="Back">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              </button>
              <h2 style={{ fontFamily: THEME.fonts.heading }} className="text-2xl font-bold text-white">Send Feedback</h2>
            </div>

            {feedbackToast && (
              <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${feedbackToast.type === 'success' ? 'bg-teal-500/10 border-teal-500/30 text-teal-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
                {feedbackToast.type === 'success' ? (
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                ) : (
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                )}
                <span className="text-sm font-medium">{feedbackToast.message}</span>
              </div>
            )}

            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-8 shadow-xl">
              <div className="mb-6">
                <label className="block text-sm text-slate-400 font-medium mb-2">Subject <span className="text-slate-600">(optional)</span></label>
                <input
                  type="text"
                  value={feedbackSubject}
                  onChange={(e) => setFeedbackSubject(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors"
                  placeholder="e.g. Food Quality, App Feature Request"
                  maxLength={100}
                  disabled={feedbackSending}
                />
              </div>
              <div className="mb-8">
                <label className="block text-sm text-slate-400 font-medium mb-2">Description <span className="text-red-400">*</span></label>
                <textarea
                  value={feedbackDescription}
                  onChange={(e) => setFeedbackDescription(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors resize-none"
                  placeholder="Tell us what's on your mind..."
                  rows={5}
                  maxLength={1000}
                  disabled={feedbackSending}
                />
                <p className="text-xs text-slate-600 mt-1 text-right">{feedbackDescription.length}/1000</p>
              </div>
              <button
                onClick={handleSubmitFeedback}
                disabled={feedbackSending || !feedbackDescription.trim()}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-bold rounded-xl transition-all text-sm"
              >
                {feedbackSending ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Sending...
                  </span>
                ) : 'Submit Feedback'}
              </button>
            </div>
          </div>
        )}

        {/* --- LEGAL CONTENT TABS (under Settings) --- */}
        {activeTab === 'settings' && (settingsSubView === 'terms' || settingsSubView === 'privacy' || settingsSubView === 'disclaimer') && (
          <LegalContentViewer
            type={settingsSubView}
            onBack={() => setSettingsSubView(null)}
          />
        )}

        {/* --- HOSTEL MESS BILLS SUB-VIEW (under Settings) --- */}
        {activeTab === 'settings' && settingsSubView === 'messBills' && (
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => setSettingsSubView(null)} className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              </button>
              <h2 style={{ fontFamily: THEME.fonts.heading }} className="text-2xl font-bold text-white">Hostel Mess Bills</h2>
            </div>

            {messBillsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 animate-pulse">
                    <div className="h-5 bg-slate-700 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-slate-700/60 rounded w-1/3" />
                  </div>
                ))}
              </div>
            ) : hostelMessBills.length > 0 ? (
              <div className="space-y-3">
                {hostelMessBills.map((bill) => (
                  <button
                    key={bill.documentId || bill.billPeriod}
                    onClick={() => setSelectedMessBill(bill)}
                    className="w-full bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 shadow-lg flex items-center justify-between text-left hover:bg-slate-700/30 transition-colors"
                  >
                    <div>
                      <h3 className="text-white font-bold text-sm sm:text-base">{bill.billPeriod || 'Unknown Period'}</h3>
                      <p className="text-slate-500 text-xs mt-0.5">{bill.hostelName || 'N/A'}</p>
                    </div>
                    <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-12 text-center shadow-xl">
                <svg className="w-16 h-16 mx-auto mb-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                <p className="text-slate-500 text-sm">No mess bills found for your hostel.</p>
              </div>
            )}
          </div>
        )}

        {/* --- YOUR ROOM SUB-VIEW (under Settings) --- */}
        {activeTab === 'settings' && settingsSubView === 'room' && (
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => setSettingsSubView(null)} className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              </button>
              <h2 style={{ fontFamily: THEME.fonts.heading }} className="text-2xl font-bold text-white">Your Room</h2>
            </div>

            {(roomData || userData?.roomNumber) ? (
              <div className="space-y-4">
                {/* Room Details Card */}
                <div className="bg-gradient-to-br from-[#1a2744] to-[#1E293B] rounded-2xl border border-indigo-500/20 p-5 shadow-xl">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30">
                      <svg className="w-6 h-6 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{userData?.hostel || 'Hostel'}</h3>
                      <p className="text-slate-400 text-xs">Room Assignment Details</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-800/60 rounded-xl p-4 text-center border border-slate-700/50">
                      <p className="text-indigo-400 text-xs font-semibold uppercase tracking-wider">Room</p>
                      <p className="text-white text-2xl font-mono font-bold mt-1">{roomData?.number || userData?.roomNumber || '—'}</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-xl p-4 text-center border border-slate-700/50">
                      <p className="text-indigo-400 text-xs font-semibold uppercase tracking-wider">Floor</p>
                      <p className="text-white text-2xl font-mono font-bold mt-1">{roomData?.floor || userData?.roomFloor || '—'}</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-xl p-4 text-center border border-slate-700/50">
                      <p className="text-indigo-400 text-xs font-semibold uppercase tracking-wider">Type</p>
                      <p className="text-white text-lg font-semibold mt-1 capitalize">{roomData?.type || '—'}</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-xl p-4 text-center border border-slate-700/50">
                      <p className="text-indigo-400 text-xs font-semibold uppercase tracking-wider">Capacity</p>
                      <p className="text-white text-2xl font-mono font-bold mt-1">{roomData?.capacity || '—'}</p>
                    </div>
                  </div>
                  {roomData?.hasAttachedWashroom && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-slate-300 bg-slate-800/40 rounded-xl px-4 py-2.5 border border-slate-700/30">
                      <span>🚿</span>
                      <span>Attached Washroom</span>
                    </div>
                  )}
                  {roomData?.features?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {roomData.features.map((f, i) => (
                        <span key={i} className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-300">{f}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Roommates Card */}
                {roommates.length > 0 && (
                  <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 shadow-lg">
                    <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-4">🤝 Roommate{roommates.length > 1 ? 's' : ''}</p>
                    <div className="space-y-3">
                      {roommates.map(rm => (
                        <div key={rm.uid} className="bg-slate-800/50 rounded-xl px-4 py-3.5 border border-slate-700/30">
                          <div className="flex items-start gap-3">
                            <div className="w-11 h-11 rounded-full bg-indigo-500/20 flex items-center justify-center text-sm font-bold text-indigo-300 flex-shrink-0 mt-0.5">
                              {rm.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-semibold truncate">{rm.name}</p>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {rm.batch && (
                                  <span className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded-md text-[10px] text-indigo-300 font-medium">
                                    {rm.batch}
                                  </span>
                                )}
                                {rm.diet && (
                                  <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-md text-[10px] text-emerald-300 font-medium">
                                    {rm.diet === 'Veg' ? '🥬 Veg' : rm.diet === 'Non-Veg' ? '🍗 Non-Veg' : `🥚 ${rm.diet}`}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 mt-2 text-xs">
                                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {rm.isPrivate ? (
                                  <span className="text-slate-600 flex items-center gap-1">
                                    ••••••••••
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                  </span>
                                ) : (
                                  <span className="text-slate-400">{rm.contact || '—'}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Room Change Request */}
                {(roomData || userData?.roomNumber) && (
                  <div className="bg-[#1E293B] rounded-2xl border border-amber-500/20 p-5 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/30">
                        <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-sm">Room Change Request</h3>
                        <p className="text-slate-500 text-xs">Request a room change from the admin</p>
                      </div>
                    </div>

                    {roomChangeRequest && roomChangeRequest.status === 'pending' ? (
                      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                          <p className="text-amber-300 text-xs font-bold uppercase tracking-wider">Request Pending</p>
                        </div>
                        <p className="text-slate-300 text-sm">Reason: {roomChangeRequest.reason}</p>
                        {roomChangeRequest.preferredRoomNumber && (
                          <p className="text-slate-400 text-xs mt-1">Preferred Room: {roomChangeRequest.preferredRoomNumber}</p>
                        )}
                        <p className="text-slate-600 text-xs mt-2">Submitted {roomChangeRequest.createdAt?.toDate?.() ? new Date(roomChangeRequest.createdAt.toDate()).toLocaleDateString() : 'recently'}</p>
                      </div>
                    ) : roomChangeRequest && roomChangeRequest.status === 'approved' ? (
                      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                          <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider">Request Approved</p>
                        </div>
                        {roomChangeRequest.adminComment && <p className="text-slate-400 text-sm">Admin: {roomChangeRequest.adminComment}</p>}
                      </div>
                    ) : roomChangeRequest && roomChangeRequest.status === 'rejected' ? (
                      <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                          <p className="text-red-300 text-xs font-bold uppercase tracking-wider">Request Rejected</p>
                        </div>
                        {roomChangeRequest.adminComment && <p className="text-slate-400 text-sm">Admin: {roomChangeRequest.adminComment}</p>}
                        <button onClick={() => setRoomChangeRequest(null)} className="mt-3 text-xs text-amber-400 hover:text-amber-300 transition-colors">Submit a new request →</button>
                      </div>
                    ) : !roomChangeWindowOpen ? (
                      <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-4 text-center">
                        <svg className="w-8 h-8 mx-auto mb-2 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <p className="text-slate-500 text-sm font-medium">Room change requests are currently closed</p>
                        <p className="text-slate-600 text-xs mt-1">The admin will open the window when available</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <textarea
                          className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-amber-500/50 focus:outline-none resize-none transition-colors"
                          rows="3"
                          placeholder="Why do you want to change your room?"
                          value={roomChangeForm.reason}
                          onChange={(e) => setRoomChangeForm(p => ({ ...p, reason: e.target.value }))}
                        />
                        <input
                          className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-amber-500/50 focus:outline-none transition-colors"
                          placeholder="Preferred room number (optional)"
                          value={roomChangeForm.preferredRoom}
                          onChange={(e) => setRoomChangeForm(p => ({ ...p, preferredRoom: e.target.value }))}
                        />
                        <button
                          onClick={async () => {
                            if (!roomChangeForm.reason.trim()) return;
                            setSubmittingRoomChange(true);
                            try {
                              const reqDoc = await addDoc(collection(db, 'roomChangeRequests'), {
                                userId: user.uid,
                                userName: userData?.name || user.displayName || '',
                                instituteId: userData?.instituteId || '',
                                hostel: userData?.hostel || '',
                                currentRoomId: userData?.roomId || '',
                                currentRoomNumber: roomData?.number || userData?.roomNumber || '',
                                currentFloor: roomData?.floor || userData?.roomFloor || '',
                                preferredRoomNumber: roomChangeForm.preferredRoom.trim(),
                                reason: roomChangeForm.reason.trim(),
                                status: 'pending',
                                adminComment: '',
                                createdAt: serverTimestamp(),
                              });
                              setRoomChangeRequest({ id: reqDoc.id, status: 'pending', reason: roomChangeForm.reason.trim(), preferredRoomNumber: roomChangeForm.preferredRoom.trim(), createdAt: { toDate: () => new Date() } });
                              setRoomChangeForm({ reason: '', preferredRoom: '' });
                            } catch (err) {
                              console.error('Failed to submit room change request:', err);
                              alert('Failed to submit request: ' + err.message);
                            }
                            setSubmittingRoomChange(false);
                          }}
                          disabled={submittingRoomChange || !roomChangeForm.reason.trim()}
                          className="w-full py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-40 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-400 hover:to-orange-400 shadow-lg shadow-amber-500/20"
                        >
                          {submittingRoomChange ? 'Submitting...' : 'Submit Room Change Request'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-12 text-center shadow-xl">
                <svg className="w-16 h-16 mx-auto mb-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p className="text-slate-400 text-sm font-medium">No Room Assigned Yet</p>
                <p className="text-slate-600 text-xs mt-1">Your room assignment will appear here once allocated by the admin.</p>
              </div>
            )}

            {/* Room Preferences Form — hidden once room is allocated */}
            {!userData?.roomId && (
            <div className="mt-6 bg-gradient-to-br from-[#1a2744] to-[#1E293B] rounded-2xl border border-amber-500/20 p-5 shadow-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Room Preferences</h3>
                  <p className="text-slate-400 text-xs">Your preferences help the admin allocate the best room for you.</p>
                </div>
              </div>

              <RoomPreferencesForm user={user} userData={userData} />
            </div>
            )}
          </div>
        )}


        {/* --- SETTINGS MAIN VIEW --- */}
        {activeTab === 'settings' && !settingsSubView && (
          <div className="max-w-2xl mx-auto">
            <h2 style={{ fontFamily: THEME.fonts.heading }} className="text-2xl font-bold text-white mb-6">Settings</h2>

            {/* Institute Card */}
            {userData && (
              <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 mb-4 shadow-lg flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{userData.instituteName || 'Institute'}</p>
                  <p className="text-slate-400 text-sm truncate">{userData.course ? `${userData.course} • ` : ''}{userData.hostel || 'Hostel'} • {userData.batch || 'Batch'}</p>
                </div>
              </div>
            )}

            {/* Settings Menu Items */}
            <div className="space-y-2">
              <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>} label="Edit Profile" onClick={() => setSettingsSubView('profile')} />
              <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>} label="Mess Committee" onClick={() => setSettingsSubView('committee')} />
              <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>} label="Send Feedback" onClick={() => setSettingsSubView('feedback')} />
              {isModuleEnabled('ROOM_ALLOCATION') && (
                <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>} label="Your Room" onClick={() => setSettingsSubView('room')} />
              )}

              <div className="pt-3 mt-3 border-t border-slate-700/50">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 px-1">Actions</p>
                <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>} label="Hostel Mess Bills" onClick={() => setSettingsSubView('messBills')} />
              </div>

              <div className="pt-3 mt-3 border-t border-slate-700/50">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 px-1">Legal</p>
                <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>} label="Terms of Service" onClick={() => setSettingsSubView('terms')} />
                <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>} label="Privacy Policy" onClick={() => setSettingsSubView('privacy')} />
                <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>} label="Disclaimer" onClick={() => setSettingsSubView('disclaimer')} />
              </div>

              <div className="pt-3 mt-3 border-t border-slate-700/50">
                <SettingsRow icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>} label="Sign Out" destructive onClick={() => setShowSignOutDialog(true)} />
              </div>
            </div>
          </div>
        )}

        {/* --- SUPPORT TAB --- */}
        {activeTab === 'support' && (
          <div className="animate-fade-in-up">
            <SupportTicketSystem user={user} userData={userData} />
          </div>
        )}

        {/* --- COMPLAINTS TAB --- */}
        {activeTab === 'complaints' && (
          <div className="animate-fade-in-up">
            <ComplaintBox userProfile={{ ...userData, uid: user?.uid, email: userData?.email || user?.email }} />
          </div>
        )}

      </main>

      {/* --- BOTTOM NAVIGATION BAR --- */}
      <nav className="fixed bottom-0 left-0 right-0 z-50" style={{ willChange: 'transform', transform: 'translateZ(0)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="bg-[#1E293B] border-t border-slate-700/50">
          <div className="max-w-lg mx-auto flex items-center justify-around h-16">
            {[
              { key: 'home', label: 'Home', moduleKey: null, icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg> },
              { key: 'history', label: 'History', moduleKey: 'BILLING', icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg> },
              { key: 'support', label: 'Support', moduleKey: 'SUPPORT_TICKETS', icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg> },
              { key: 'complaints', label: 'Complaints', moduleKey: 'COMPLAINTS', icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg> },
              { key: 'settings', label: 'Settings', moduleKey: null, icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  // Guard module-protected tabs
                  if (tab.moduleKey) {
                    requireModule(tab.moduleKey, () => {
                      setActiveTab(tab.key);
                      if (tab.key !== 'settings') setSettingsSubView(null);
                    });
                  } else {
                    setActiveTab(tab.key);
                    if (tab.key !== 'settings') setSettingsSubView(null);
                  }
                }}
                className={`flex flex-col items-center justify-center gap-0.5 px-4 py-1 rounded-xl transition-all relative ${activeTab === tab.key ? 'text-amber-500' : 'text-slate-500 hover:text-slate-300'}`}
              >
                {tab.icon}
                <span className="text-[10px] font-semibold">{tab.label}</span>
                {activeTab === tab.key && <div className="w-1 h-1 rounded-full bg-amber-500 mt-0.5" />}
                {/* [FEATURE] Notification badge for pending bills on History tab */}
                {tab.key === 'home' && pendingBillsCount > 0 && activeTab !== 'home' && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 shadow-lg">
                    {pendingBillsCount > 9 ? '9+' : pendingBillsCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* --- HEALTH CHECK OVERLAY --- */}
      {paymentState === 'checking_health' && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#1E293B] rounded-2xl shadow-2xl p-8 max-w-sm w-full border border-slate-700 text-center">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <div className="absolute inset-0 rounded-full border-2 border-amber-500/20"></div>
              <div className="absolute inset-0 rounded-full border-2 border-t-amber-500 animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-7 h-7 text-amber-500 animate-pulse-glow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Pre-Payment Checks</h3>
            <p className="text-slate-400 text-sm">Verifying payment server availability...</p>
          </div>
        </div>
      )}

      {/* --- SERVER OFFLINE DIALOG --- */}
      {showServerOffline && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#1E293B] rounded-2xl shadow-2xl p-8 max-w-sm w-full border border-slate-700 text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center text-red-400 mb-4 border border-red-500/20 mx-auto">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Server Unavailable</h3>
            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
              The payment server is currently unreachable. Please check your internet connection or try again later.
            </p>
            <button
              onClick={() => setShowServerOffline(false)}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-xl transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* --- RECEIPT PREVIEW MODAL --- */}
      {previewBill && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 bg-slate-100 border-b">
              <h3 className="text-lg font-bold text-slate-800">Receipt Preview</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadReceiptAsPdf(previewBill, userData, previewBill.id)}
                  className="px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
                >
                  Download PDF
                </button>
                <button
                  onClick={() => setPreviewBill(null)}
                  className="p-2 text-slate-500 hover:text-slate-800 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <ReceiptPreviewFrame bill={previewBill} userData={userData} />
            </div>
          </div>
        </div>
      )}

      {/* --- PAYMENT CONFIRMATION DIALOG --- */}
      {paymentConfirmBill && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#1E293B] rounded-2xl shadow-2xl p-6 max-w-sm w-full border border-slate-700">
            <h3 style={{ fontFamily: THEME.fonts.heading }} className="text-xl font-bold text-white mb-4">Confirm Payment</h3>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm"><span className="text-slate-400">Bill Period</span><span className="text-white font-medium">{paymentConfirmBill.id}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Bill Amount</span><span className="text-white font-medium">₹{paymentConfirmBill.billAmount || 0}</span></div>
              {(paymentConfirmBill.fineAmount > 0) && (
                <div className="flex justify-between text-sm"><span className="text-slate-400">Fine</span><span className="text-red-400 font-medium">₹{paymentConfirmBill.fineAmount}</span></div>
              )}
              <div className="border-t border-slate-700 pt-3 flex justify-between text-sm">
                <span className="text-slate-300 font-bold">Total</span>
                <span className="text-amber-500 font-bold text-lg">₹{(paymentConfirmBill.billAmount || 0) + (paymentConfirmBill.fineAmount || 0)}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setPaymentConfirmBill(null)}
                className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-xl transition-all text-sm"
              >Cancel</button>
              <button
                onClick={() => { const bill = paymentConfirmBill; setPaymentConfirmBill(null); handlePayNow(bill); }}
                className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold rounded-xl transition-all text-sm"
              >Confirm Payment</button>
            </div>
          </div>
        </div>
      )}

      {/* --- DIET CHANGE DIALOG --- */}
      {showDietDialog && userData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#1E293B] rounded-2xl shadow-2xl p-6 max-w-sm w-full border border-slate-700">
            <h3 style={{ fontFamily: THEME.fonts.heading }} className="text-xl font-bold text-white mb-2">Request Diet Change</h3>
            <p className="text-slate-400 text-sm mb-6">
              Do you want to request a change from <span className="text-white font-medium">{userData.diet || 'N/A'}</span> to <span className="text-amber-400 font-medium">{userData.diet === 'Veg' ? 'Non-Veg' : 'Veg'}</span>? This will require admin approval.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDietDialog(false)}
                disabled={dietSubmitting}
                className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-xl transition-all text-sm disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={() => handleSubmitDietChange(userData.diet === 'Veg' ? 'Non-Veg' : 'Veg')}
                disabled={dietSubmitting}
                className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold rounded-xl transition-all text-sm disabled:opacity-50"
              >{dietSubmitting ? 'Submitting...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}

      {/* --- SIGN OUT CONFIRMATION DIALOG --- */}
      {showSignOutDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#1E293B] rounded-2xl shadow-2xl p-6 max-w-sm w-full border border-slate-700">
            <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center text-red-400 mx-auto mb-4 border border-red-500/20">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            </div>
            <h3 style={{ fontFamily: THEME.fonts.heading }} className="text-xl font-bold text-white text-center mb-2">Sign Out</h3>
            <p className="text-slate-400 text-sm text-center mb-6">Are you sure you want to sign out of your account?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSignOutDialog(false)}
                className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-xl transition-all text-sm"
              >Cancel</button>
              <button
                onClick={() => { setShowSignOutDialog(false); handleLogout(); }}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-all text-sm"
              >Sign Out</button>
            </div>
          </div>
        </div>
      )}

      {/* --- [MODULE SYSTEM] ModuleDisabledModal global host --- */}
      {moduleDisabledKey && (
        <ModuleDisabledModal
          moduleKey={moduleDisabledKey}
          onClose={() => setModuleDisabledKey(null)}
        />
      )}

      {/* --- MESS BILL DETAIL DIALOG --- */}
      {selectedMessBill && (() => {
        const formatRupees = (v) => v != null ? `\u20b9${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
        const formatCount = (v) => v != null ? v.toLocaleString('en-IN') : 'N/A';
        const DetailRow = ({ label, value, isTotal, isSm }) => (
          <div className={`flex justify-between py-1 ${isTotal ? 'border-t border-slate-700/50 pt-2 mt-1' : ''}`}>
            <span className={`${isSm ? 'text-xs' : 'text-sm'} ${isTotal ? 'font-bold text-amber-400' : 'text-slate-400'}`}>{label}</span>
            <span className={`${isSm ? 'text-xs' : 'text-sm'} ${isTotal ? 'font-bold text-amber-400' : 'text-white'}`}>{value}</span>
          </div>
        );
        const Section = ({ title, children }) => (
          <div className="mb-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-amber-500 mb-2">{title}</h4>
            <div className="bg-slate-900/50 rounded-xl p-3 space-y-0.5">{children}</div>
          </div>
        );
        const bill = selectedMessBill;
        return (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div className="bg-[#1E293B] rounded-2xl shadow-2xl max-w-md w-full border border-slate-700 max-h-[85vh] flex flex-col">
              <div className="p-5 border-b border-slate-700/50 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 style={{ fontFamily: THEME.fonts.heading }} className="text-lg font-bold text-white">{bill.billPeriod || 'Bill Details'}</h3>
                  <p className="text-slate-500 text-xs mt-0.5">{bill.hostelName || 'N/A'}</p>
                </div>
                <button onClick={() => setSelectedMessBill(null)} className="p-2 text-slate-500 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <Section title="Expenditure Details">
                  <DetailRow label="Groceries" value={formatRupees(bill.groceriesCost)} />
                  <DetailRow label="Vegetables" value={formatRupees(bill.vegetablesCost)} />
                  <DetailRow label="Cooking Gas" value={formatRupees(bill.cookingGasCost)} />
                  <DetailRow label="Milk & Curd" value={formatRupees(bill.milkAndCurdCost)} />
                  <DetailRow label="Token Charges" value={formatRupees((bill.autoRebateRevenue || 0) + (bill.manualTokenCharges || 0))} />
                  <DetailRow label="Newspaper" value={formatRupees(bill.newspaperCost)} />
                  <DetailRow label="Municipal Waste" value={formatRupees(bill.municipalWasteCost)} />
                  <DetailRow label="Total Expenditure" value={formatRupees(bill.totalExpenditure)} isTotal />
                </Section>
                <Section title="Diet-Specific Costs">
                  <DetailRow label="Non-Veg Total" value={formatRupees(bill.nonVegTotalCost)} />
                  <DetailRow label="Veg Total" value={formatRupees(bill.vegTotalCost)} />
                </Section>
                <Section title="Student Participation">
                  <DetailRow label="Total Students" value={formatCount(bill.totalStudentsNoLoan)} />
                  <DetailRow label="Non-Veg Students" value={formatCount(bill.notRealNonVegetarianStudentsCount)} />
                  <DetailRow label="Veg Students" value={formatCount(bill.notRealVegetarianStudentsCount)} />
                </Section>
                <Section title="Salary & Calculations">
                  <DetailRow label="Total Staff Salaries" value={formatRupees(bill.totalStaffSalaries)} />
                  <DetailRow label="Salary / Student" value={formatRupees(bill.salaryCostPerStudent)} />
                  <DetailRow label="Exp. (excl. salary) / Student" value={formatRupees(bill.expenditureExcludingSalariesPerStudent)} isSm />
                  <DetailRow label="Non-Veg Cost / Student" value={formatRupees(bill.nonVegCostPerStudent)} />
                  <DetailRow label="Veg Cost / Student" value={formatRupees(bill.vegCostPerStudent)} />
                </Section>
                <Section title="Final Bill Per Student">
                  <DetailRow label="Non-Veg (excl. concession)" value={formatRupees(bill.finalAmountPerNonVegetarianWithoutConcession)} isTotal isSm />
                  <DetailRow label="Veg (excl. concession)" value={formatRupees(bill.finalAmountPerVegetarianWithoutConcession)} isTotal isSm />
                </Section>
              </div>
              <div className="p-4 border-t border-slate-700/50 flex-shrink-0">
                <button
                  onClick={() => setSelectedMessBill(null)}
                  className="w-full py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-xl transition-all text-sm"
                >Close</button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

// --- SUB-COMPONENT: Stat Card ---
const StatCard = ({ title, value, icon, color, subtext }) => (
  <div className="bg-[#1E293B] p-6 rounded-2xl border border-slate-700/50 shadow-lg flex items-start justify-between group hover:border-slate-600 transition-colors">
    <div>
      <p className="text-slate-400 text-sm font-medium mb-1">{title}</p>
      <h3 className="text-3xl font-bold text-white font-heading">{value}</h3>
      {subtext && <p className="text-xs text-teal-400 mt-2 font-medium">{subtext}</p>}
    </div>
    <div className={`p-3 rounded-xl bg-slate-800 border border-slate-700 group-hover:bg-slate-700 transition-colors`}>
      {icon}
    </div>
  </div>
);

// --- SUB-COMPONENT: Profile Detail Row ---
const ProfileDetail = ({ label, value, highlight }) => (
  <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-800">
    <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">{label}</p>
    <p className={`font-medium ${highlight ? 'text-amber-400' : 'text-slate-200'}`}>
      {value || "Not Set"}
    </p>
  </div>
);

// --- SUB-COMPONENT: Settings Row ---
// --- SUB-COMPONENT: Room Preferences Form (Dynamic + Lifestyle) ---
const LIFESTYLE_QUESTIONS = [
  { key: 'sleepSchedule', title: 'Sleep Schedule', subtitle: 'When do you usually sleep?', emoji: '😴',
    options: [{ value: 'early_bird', label: 'Early Bird', emoji: '🌅' }, { value: 'night_owl', label: 'Night Owl', emoji: '🦉' }, { value: 'flexible', label: 'Flexible', emoji: '🔄' }] },
  { key: 'cleanlinessLevel', title: 'Cleanliness', subtitle: 'How tidy do you keep your space?', emoji: '🧹',
    options: [{ value: 'very_tidy', label: 'Very Tidy', emoji: '✨' }, { value: 'moderate', label: 'Moderate', emoji: '🧹' }, { value: 'relaxed', label: 'Relaxed', emoji: '😌' }] },
  { key: 'studyHabits', title: 'Study Habits', subtitle: 'Where do you prefer studying?', emoji: '📚',
    options: [{ value: 'in_room_silence', label: 'Room (Silence)', emoji: '🤫' }, { value: 'in_room_music', label: 'Room (Music)', emoji: '🎧' }, { value: 'library', label: 'Library', emoji: '📚' }, { value: 'flexible', label: 'Flexible', emoji: '🔄' }] },
  { key: 'socialStyle', title: 'Social Style', subtitle: 'How social are you?', emoji: '👥',
    options: [{ value: 'introvert', label: 'Introvert', emoji: '🧘' }, { value: 'extrovert', label: 'Extrovert', emoji: '🎉' }, { value: 'ambivert', label: 'Ambivert', emoji: '⚖️' }] },
  { key: 'noiseTolerance', title: 'Noise Tolerance', subtitle: 'How much noise can you handle?', emoji: '🔊',
    options: [{ value: 'silent', label: 'Need Silence', emoji: '🤐' }, { value: 'moderate', label: 'Some Noise OK', emoji: '🔉' }, { value: 'noisy_ok', label: 'Noisy is Fine', emoji: '🔊' }] },
];

const ALL_ROOM_TYPE_DETAILS = {
  single: { label: 'Single', desc: '1 Bed • Private', people: '👤' },
  double: { label: 'Double', desc: '2 Beds • Shared', people: '👥' },
  triple: { label: 'Triple', desc: '3 Beds • Shared', people: '👥👤' },
  quad: { label: 'Quad', desc: '4 Beds • Shared', people: '👥👥' },
  dormitory: { label: 'Dormitory', desc: '8 Beds • Shared', people: '👥👥👥👥' },
};

const RoomPreferencesForm = ({ user, userData }) => {
  const [roomType, setRoomType] = useState(userData?.preferredRoomType || '');
  const [floor, setFloor] = useState(userData?.preferredFloor || '');
  const [roommate, setRoommate] = useState(userData?.roommatePreference || '');
  const [lifestyle, setLifestyle] = useState(userData?.lifestyle || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Dynamic data
  const [loading, setLoading] = useState(true);
  const [prefWindowOpen, setPrefWindowOpen] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableFloors, setAvailableFloors] = useState([]);

  // Fetch dynamic data on mount
  useEffect(() => {
    const fetchDynamicData = async () => {
      const instId = userData?.instituteId;
      const hostel = userData?.hostel;
      if (!instId || !hostel) { setLoading(false); return; }
      try {
        // 1. Check preference window
        const instSnap = await getDoc(doc(db, 'institutes', instId));
        if (instSnap.exists()) {
          setPrefWindowOpen(instSnap.data().preferenceWindowOpen || false);
        }
        // 2. Fetch rooms for this hostel
        const roomsSnap = await getDocs(
          query(collection(db, 'institutes', instId, 'rooms'),
            where('hostel', '==', hostel))
        );
        const rooms = roomsSnap.docs.map(d => d.data());
        // Extract unique room types
        setAvailableTypes([...new Set(rooms.map(r => r.type).filter(Boolean))].sort());
        // Extract floors filtered by batch restriction
        const batch = userData?.batch;
        const filteredFloors = rooms.filter(room => {
          const restriction = room.batchRestriction;
          if (!restriction?.enabled || !batch) return true;
          const batches = restriction.batches || [];
          const mode = restriction.mode || 'include';
          return mode === 'include' ? batches.includes(String(batch)) : !batches.includes(String(batch));
        }).map(r => r.floor).filter(Boolean);
        setAvailableFloors([...new Set(filteredFloors)].sort((a, b) => a - b));
      } catch (err) {
        console.error('Failed to load room data:', err);
      }
      setLoading(false);
    };
    fetchDynamicData();
  }, [userData?.instituteId, userData?.hostel, userData?.batch]);

  const handleSave = async () => {
    if (!user?.uid) return;
    setSaving(true);
    setSaved(false);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        preferredRoomType: roomType || null,
        preferredFloor: floor || null,
        roommatePreference: roommate || null,
        lifestyle: Object.keys(lifestyle).length > 0 ? lifestyle : null,
        preferencesSubmitted: true,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      console.error('Failed to save room preferences:', err);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="w-6 h-6 animate-spin text-amber-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (!prefWindowOpen) {
    return (
      <div className="text-center py-8 px-4">
        <div className="w-16 h-16 rounded-full bg-slate-800/60 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-white font-bold text-lg mb-1">Preferences Window Closed</p>
        <p className="text-slate-500 text-sm leading-relaxed">Your admin hasn't opened the preference submission window yet. You'll be notified when it's time to submit.</p>
      </div>
    );
  }

  const hasSubmittedPreferences = userData?.preferencesSubmitted || !!userData?.preferredRoomType;

  if (hasSubmittedPreferences) {
    const roomTypeLabel = {
      single: { emoji: '🛏️', name: 'Single Room', desc: '1 Bed • Private' },
      double: { emoji: '🛏️🛏️', name: 'Double Room', desc: '2 Beds • Shared' },
      triple: { emoji: '🛏️🛏️🛏️', name: 'Triple Room', desc: '3 Beds • Shared' },
    }[userData?.preferredRoomType] || { emoji: '🏠', name: userData?.preferredRoomType || 'Not selected', desc: '' };

    return (
      <div className="space-y-5">
        {/* Header */}
        <div className="text-center pb-2">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/10 animate-pulse" />
            <div className="relative w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
          </div>
          <h3 className="text-white font-bold text-xl">Preferences Submitted</h3>
          <p className="text-slate-500 text-xs mt-1.5 max-w-xs mx-auto leading-relaxed">Your preferences have been recorded and will be considered during room allocation.</p>
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-b from-slate-800/60 to-slate-800/30 border border-slate-700/40 rounded-2xl overflow-hidden">
          {/* Room Type — Featured */}
          <div className="p-5 border-b border-slate-700/30">
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-3">Room Type</p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-xl flex-shrink-0">
                {roomTypeLabel.emoji}
              </div>
              <div>
                <p className="text-white font-bold text-base">{roomTypeLabel.name}</p>
                {roomTypeLabel.desc && <p className="text-slate-500 text-[11px]">{roomTypeLabel.desc}</p>}
              </div>
            </div>
          </div>

          {/* Two-column row */}
          <div className="grid grid-cols-2 divide-x divide-slate-700/30 border-b border-slate-700/30">
            <div className="p-4">
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-1.5">Floor</p>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                </div>
                <p className="text-white font-semibold text-sm">{userData?.preferredFloor ? `Floor ${userData.preferredFloor}` : 'Any'}</p>
              </div>
            </div>
            <div className="p-4">
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-1.5">Roommate</p>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                </div>
                <p className="text-white font-semibold text-sm truncate">{userData?.roommatePreference || 'None'}</p>
              </div>
            </div>
          </div>

          {/* Lifestyle Section */}
          {userData?.lifestyle && Object.keys(userData.lifestyle).length > 0 && (
            <div className="p-5">
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-3">Lifestyle Preferences</p>
              <div className="grid grid-cols-1 gap-2">
                {LIFESTYLE_QUESTIONS.map(q => {
                  const val = userData.lifestyle[q.key];
                  const opt = q.options.find(o => o.value === val);
                  if (!opt) return null;
                  return (
                    <div key={q.key} className="flex items-center gap-2.5 bg-slate-900/40 rounded-xl px-3 py-2">
                      <span className="text-lg">{opt.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-[9px] uppercase tracking-wider">{q.title}</p>
                        <p className="text-white text-xs font-medium">{opt.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Info Footer */}
        <div className="flex items-start gap-2.5 bg-slate-800/30 border border-slate-700/20 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-slate-500 text-[11px] leading-relaxed">Preferences can only be submitted once and cannot be modified. Final room assignments are at your hostel administration's discretion.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Room Type Selection — Dynamic */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" /></svg>
          Room Type (available in your hostel)
        </p>
        {availableTypes.length === 0 ? (
          <p className="text-slate-600 text-xs italic">No rooms configured in your hostel yet.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {availableTypes.map(type => {
              const detail = ALL_ROOM_TYPE_DETAILS[type] || { label: type, desc: type, people: '👤' };
              return (
                <button
                  key={type}
                  onClick={() => setRoomType(type)}
                  className={`relative p-4 rounded-2xl border-2 transition-all duration-200 text-center ${
                    roomType === type
                      ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/10 scale-[1.02]'
                      : 'border-slate-700/50 bg-slate-800/40 hover:border-slate-600 hover:bg-slate-800/60'
                  }`}
                >
                  {roomType === type && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                    </div>
                  )}
                  <span className="text-2xl block mb-1">{detail.people}</span>
                  <p className={`font-bold text-sm ${roomType === type ? 'text-amber-400' : 'text-white'}`}>{detail.label}</p>
                  <p className="text-slate-500 text-[10px] mt-0.5">{detail.desc}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Floor Preference — Dynamic chips */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
          Floor (available for your batch)
        </p>
        {availableFloors.length === 0 ? (
          <p className="text-slate-600 text-xs italic">No floors available for your batch.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableFloors.map(f => (
              <button
                key={f}
                onClick={() => setFloor(floor === String(f) ? '' : String(f))}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  floor === String(f)
                    ? 'bg-amber-500/15 text-amber-400 border border-amber-500/50'
                    : 'bg-slate-800/40 text-slate-400 border border-slate-700/50 hover:border-slate-600'
                }`}
              >
                {floor === String(f) && '✓ '}Floor {f}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lifestyle Questions */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
          Lifestyle & Compatibility
        </p>
        <div className="space-y-3">
          {LIFESTYLE_QUESTIONS.map(q => (
            <div key={q.key} className="bg-slate-800/30 border border-slate-700/30 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2.5">
                <span className="text-lg">{q.emoji}</span>
                <div>
                  <p className="text-white text-sm font-bold">{q.title}</p>
                  <p className="text-slate-500 text-[10px]">{q.subtitle}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {q.options.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setLifestyle(prev => ({ ...prev, [q.key]: opt.value }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      lifestyle[q.key] === opt.value
                        ? 'bg-amber-500/15 text-amber-400 border border-amber-500/40'
                        : 'bg-slate-800/50 text-slate-400 border border-transparent hover:border-slate-600'
                    }`}
                  >
                    {opt.emoji} {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Roommate Preference */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
          Preferred Roommate
        </p>
        <div className="flex items-start gap-2 bg-indigo-500/8 border border-indigo-500/15 rounded-xl px-3 py-2.5 mb-3">
          <svg className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-indigo-300/70 text-[11px] leading-relaxed">Both you and your preferred roommate must request each other for the match to work.</p>
        </div>
        <input
          type="email"
          value={roommate}
          onChange={e => setRoommate(e.target.value)}
          placeholder="friend@example.com"
          className="w-full bg-slate-800/60 border border-slate-700/50 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-600 focus:border-amber-500/50 focus:outline-none focus:ring-1 focus:ring-amber-500/20 transition-all"
        />
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {saved && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3"
          >
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
            </div>
            <div>
              <p className="text-emerald-400 text-sm font-bold">Preferences Saved! ✨</p>
              <p className="text-emerald-500/60 text-[11px]">Your admin will use these during room allocation.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Button */}
      <button
        onClick={() => setShowConfirm(true)}
        disabled={saving}
        className="w-full py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-50 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg shadow-amber-500/20 active:scale-[0.98]"
      >
        {saving ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            Saving...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
            Save Preferences
          </span>
        )}
      </button>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-slate-900 border border-slate-700/50 rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <p className="text-white font-bold text-lg">Please Note</p>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">
                These are your preferences and will be considered during room allocation. However, final room assignments are at the discretion of your hostel administration and subject to availability. Submitting preferences does not guarantee a specific room, floor, or roommate.
              </p>
              <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5 mb-6">
                <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                <p className="text-amber-300 text-[11px] font-semibold leading-relaxed">Preferences can only be submitted once and cannot be changed after saving.</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowConfirm(false)} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-400 border border-slate-700/50 hover:bg-slate-800 transition-all">
                  Cancel
                </button>
                <button onClick={() => { setShowConfirm(false); handleSave(); }} className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/20 transition-all hover:from-amber-600 hover:to-amber-700">
                  Save Preferences
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SettingsRow = ({ icon, label, onClick, destructive }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all ${destructive
      ? 'text-red-400 hover:bg-red-500/10'
      : 'text-slate-300 hover:bg-slate-800'
      }`}
  >
    <span className={destructive ? 'text-red-400' : 'text-slate-400'}>{icon}</span>
    <span className="flex-1 text-left text-sm font-medium">{label}</span>
    <svg className="w-4 h-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
  </button>
);

// --- SUB-COMPONENT: Legal Content Viewer ---
const LEGAL_PAGES = {
  terms: { title: 'Terms of Service', path: '/terms-of-service' },
  privacy: { title: 'Privacy Policy', path: '/privacy-policy' },
  disclaimer: { title: 'Disclaimer', path: '/disclaimer' },
};

const LegalContentViewer = ({ type, onBack }) => {
  const page = LEGAL_PAGES[type];
  if (!page) return null;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
          title="Back to Overview"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h2 style={{ fontFamily: "'Poppins', sans-serif" }}
          className="text-2xl font-bold text-white">
          {page.title}
        </h2>
      </div>

      <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden"
        style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
        <iframe
          src={page.path}
          title={page.title}
          className="w-full h-full border-0"
          style={{
            backgroundColor: '#0F172A',
            colorScheme: 'dark',
          }}
        />
      </div>
    </div>
  );
};

// --- SUB-COMPONENT: Receipt Preview (rendered inline via iframe srcdoc) ---
const ReceiptPreviewFrame = ({ bill, userData }) => {
  const html = generateReceiptHtml(bill, userData, bill.id);
  return (
    <iframe
      srcDoc={html}
      title="Receipt Preview"
      className="w-full border-0"
      style={{ minHeight: '700px', background: '#fff' }}
    />
  );
};

export default Dashboard;